import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Sparkles, Bookmark, Send, Trash2, Copy, Store, Tag, Percent, TrendingUp, Link2, Loader2,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import ScoreBadge from "@/components/ScoreBadge";
import { scoreDeal, updateDeal, deleteDeal, generateContent } from "@/lib/api";

const Metric = ({ icon: Icon, label, value, accent = "#86868B" }) => (
  <div className="bg-[#F5F5F7] rounded-xl p-3">
    <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#86868B] uppercase tracking-wide mb-1">
      <Icon className="h-3.5 w-3.5" strokeWidth={2} style={{ color: accent }} />
      {label}
    </div>
    <p className="font-heading font-semibold text-[#1D1D1F]">{value}</p>
  </div>
);

export const DealDetailSheet = ({ deal, open, onOpenChange, onChanged }) => {
  const [local, setLocal] = useState(deal);
  const [scoring, setScoring] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => setLocal(deal), [deal]);
  if (!local) return null;

  const refresh = (updated) => {
    setLocal(updated);
    onChanged?.(updated);
  };

  const handleScore = async () => {
    setScoring(true);
    try {
      const updated = await scoreDeal(local.id);
      refresh(updated);
      toast.success(`KI-Score: ${updated.ai_score}/100`);
    } catch (e) {
      toast.error("KI-Bewertung fehlgeschlagen");
    } finally {
      setScoring(false);
    }
  };

  const handleStatus = async (status, label) => {
    try {
      const updated = await updateDeal(local.id, { status });
      refresh(updated);
      toast.success(label);
    } catch (e) {
      toast.error("Aktualisierung fehlgeschlagen");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDeal(local.id);
      toast.success("Deal gelöscht");
      onOpenChange(false);
      onChanged?.(null);
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const updated = await generateContent(local.id, {
        tone: "freundlich & verkaufsstark",
        platform: "Telegram",
        affiliate_tag: "deinkanal-21",
      });
      refresh(updated);
      toast.success("Content generiert");
    } catch (e) {
      toast.error("Content-Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const gc = local.generated_content;

  const copyPost = () => {
    if (!gc) return;
    const text = `${gc.headline}\n\n${gc.description}\n\n💶 Nur ${local.deal_price.toFixed(2)} € statt ${local.original_price.toFixed(2)} €\n👉 ${gc.cta}\n${local.affiliate_url}\n\n${gc.hashtags.join(" ")}`;
    navigator.clipboard.writeText(text);
    toast.success("Post in Zwischenablage kopiert");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto p-0 bg-white"
        data-testid="deal-detail-sheet"
      >
        <div className="relative h-52 bg-[#F5F5F7]">
          <img src={local.image_url} alt={local.product_name} className="h-full w-full object-cover" />
          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center rounded-full bg-[#1D1D1F] text-white px-3 py-1 text-sm font-bold">
              -{local.discount_percent}%
            </span>
          </div>
        </div>

        <div className="p-6">
          <SheetHeader className="text-left space-y-1 mb-4">
            <div className="flex items-center gap-1.5 text-xs text-[#86868B]">
              <Store className="h-3.5 w-3.5" strokeWidth={2} /> {local.source} · {local.category}
            </div>
            <SheetTitle className="font-heading text-xl font-bold tracking-tight text-[#1D1D1F]">
              {local.product_name}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Deal-Details, KI-Bewertung und Aktionen für {local.product_name}
            </SheetDescription>
            <p className="text-sm text-[#86868B]">{local.description}</p>
          </SheetHeader>

          <div className="flex items-baseline gap-3 mb-5">
            <span className="font-heading text-3xl font-bold text-[#1D1D1F] tracking-tight">
              {local.deal_price.toFixed(2)} €
            </span>
            <span className="text-base text-[#A1A1A6] line-through">{local.original_price.toFixed(2)} €</span>
            <div className="ml-auto"><ScoreBadge score={local.ai_score} size="lg" /></div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-5">
            <Metric icon={Percent} label="Rabatt" value={`${local.discount_percent}%`} accent="#FF3B30" />
            <Metric icon={Tag} label="Provision" value={`${local.commission_percent}%`} accent="#0071E3" />
            <Metric icon={TrendingUp} label="Marge" value={`${local.est_margin_percent}%`} accent="#34C759" />
          </div>

          {local.ai_score_reason && (
            <div className="rounded-xl border border-[#A259FF]/20 bg-[#A259FF]/[0.06] p-3 mb-5">
              <p className="text-xs font-semibold ai-gradient-text mb-1 inline-flex items-center gap-1">
                <Sparkles className="h-3.5 w-3.5" /> KI-Einschätzung
              </p>
              <p className="text-sm text-[#1D1D1F]">{local.ai_score_reason}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mb-3">
            <Button
              onClick={handleScore}
              disabled={scoring}
              className="rounded-full bg-white border border-[#A259FF]/30 text-[#1D1D1F] hover:shadow-[0_0_15px_rgba(162,89,255,0.2)]"
              data-testid="deal-score-button"
            >
              {scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {local.ai_score != null ? "Neu bewerten" : "KI bewerten"}
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="rounded-full ai-gradient-bg text-white hover:opacity-90"
              data-testid="deal-generate-button"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Content
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-5">
            <Button
              variant="secondary"
              onClick={() => handleStatus("saved", "Deal gespeichert")}
              className="rounded-full bg-black/5 hover:bg-black/10 text-[#1D1D1F]"
              data-testid="deal-save-button"
            >
              <Bookmark className="h-4 w-4" /> Speichern
            </Button>
            <Button
              onClick={() => handleStatus("posted", "Als gepostet markiert")}
              className="rounded-full bg-[#0071E3] hover:bg-[#0077ED] text-white"
              data-testid="deal-post-button"
            >
              <Send className="h-4 w-4" /> Gepostet
            </Button>
          </div>

          {gc && (
            <div className="rounded-2xl border border-black/5 bg-[#F5F5F7] p-4 mb-4" data-testid="deal-generated-content">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide">Generierter Post</p>
                <Button size="sm" variant="ghost" onClick={copyPost} className="h-7 text-xs rounded-full" data-testid="deal-copy-post">
                  <Copy className="h-3.5 w-3.5" /> Kopieren
                </Button>
              </div>
              <p className="font-heading font-semibold text-[#1D1D1F] mb-1">{gc.headline}</p>
              <p className="text-sm text-[#1D1D1F] whitespace-pre-line mb-2">{gc.description}</p>
              <p className="text-sm font-medium text-[#0071E3] mb-2">{gc.cta}</p>
              <div className="flex flex-wrap gap-1.5">
                {gc.hashtags.map((h, i) => (
                  <span key={i} className="text-xs text-[#86868B] bg-white rounded-full px-2 py-0.5 border border-black/5">{h}</span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-[#86868B] mb-5 break-all">
            <Link2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{local.affiliate_url}</span>
          </div>

          <Separator className="mb-4" />

          <Button
            variant="ghost"
            onClick={handleDelete}
            className="w-full rounded-full text-[#FF3B30] hover:bg-rose-50 hover:text-[#FF3B30]"
            data-testid="deal-delete-button"
          >
            <Trash2 className="h-4 w-4" /> Deal löschen
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DealDetailSheet;
